export type S3UploadOptions = {
  bucket: string;
  acl: string;
  awsaccesskeyid: string;
  expires: string;
  key: string;
  policy: string;
  signature: string;
  success_action_status: string;
  'Content-Type': string;
};

class S3UploadError extends Error {
  detail: string;
  status: number;

  constructor({ detail, status }: { detail: string; status: number }, message?: string) {
    super(message);
    this.name = 'S3UploadError';
    this.detail = detail;
    this.status = status;
  }
}

/**
 * Uploads a file to AWS S3 using a signed POST policy.
 *
 * Ported from the Ember `utils/uploads/s3` helper. Non-EU datacenters upload to
 * S3; see `getUploadFn` for how the target is chosen.
 */
export async function uploadToS3(
  file: File,
  json: S3UploadOptions,
  options: { processData?: (data: FormData) => void } = {},
): Promise<{ key: string }> {
  const data = new FormData();

  for (const key of Object.keys(json)) {
    // `bucket` only forms the URL; it's not a valid S3 POST form field and AWS
    // rejects fields without a matching policy condition.
    if (key === 'bucket') {
      continue;
    }
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    data.append(key, json[key as keyof S3UploadOptions]);
  }

  options.processData?.(data);
  data.append('file', file);

  const response = await fetch(`https://s3.amazonaws.com/${json.bucket}`, { method: 'POST', body: data });
  const text = await response.text();
  const xml = new DOMParser().parseFromString(text, 'text/xml');

  if (response.ok) {
    return { key: xml.getElementsByTagName('Location')[0]?.textContent ?? '' };
  }

  const detail =
    xml.getElementsByTagName('Message')[0]?.textContent ?? 'Something went wrong while uploading to AWS S3.';
  throw new S3UploadError({ detail, status: response.status }, text);
}
